/* ============================================================
   CHIPFIXX — Cart drawer behaviour
   File: assets/cf-cart-drawer.js
   Built 2026-07-28. Loaded globally from layout/theme.liquid,
   after Dawn's cart-drawer.js.

   Two jobs:

   1. Paint the colour chips. The hex arrives as escaped data on the
      element and is only applied after it matches a strict pattern.
      It originates from a URL query param on the paint-code pages
      (?hex=...), so it is untrusted input and must never be written
      straight into a style attribute server-side.

   2. Expose window.CFCart so other surfaces (the kit builder in
      sections/kit-selector.liquid) can refresh and open the drawer
      instead of doing a full page load to /cart.
   ============================================================ */

(function () {
  'use strict';

  var HEX = /^#[0-9a-f]{6}$/i;
  var CART_URL = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart';

  /* ── 1. Colour chips ──────────────────────────────────── */

  function paintChips(root) {
    var chips = (root || document).querySelectorAll('.cf-chip[data-cf-hex]');

    for (var i = 0; i < chips.length; i++) {
      var chip = chips[i];
      var raw = (chip.getAttribute('data-cf-hex') || '').trim();

      /* Tolerate a stored value that lost its leading '#'. */
      if (raw && raw.charAt(0) !== '#') raw = '#' + raw;

      if (!HEX.test(raw)) continue; /* leave the neutral fallback in place */

      chip.style.backgroundColor = raw;
      chip.classList.remove('cf-chip--unknown');
    }
  }

  /* ── 2. Flash newly added lines ───────────────────────────
     A second kit added to a drawer that already has one is otherwise
     a silent change. Diff the line keys across renders and mark what
     is new so the animation in cf-cart-drawer.css can run. */

  var seenKeys = null; /* null until the first observation */

  function markNewLines() {
    var rows = document.querySelectorAll('cart-drawer .cart-item[data-cf-key]');
    var current = [];
    var i;

    for (i = 0; i < rows.length; i++) {
      current.push(rows[i].getAttribute('data-cf-key'));
    }

    if (seenKeys !== null) {
      for (i = 0; i < rows.length; i++) {
        var key = rows[i].getAttribute('data-cf-key');
        if (seenKeys.indexOf(key) === -1) {
          rows[i].setAttribute('data-cf-new', '');
        }
      }
    }

    seenKeys = current;
  }

  /* ── 3. Re-apply after Dawn re-renders the drawer ─────────
     Dawn replaces #CartDrawer's innerHTML wholesale on quantity
     change, add-to-cart and remove, which throws away the inline
     background colours. A subtree observer catches every path,
     including ones added by future theme work. */

  function watch() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    paintChips(drawer);
    markNewLines();

    var queued = false;
    var observer = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        paintChips(drawer);
        markNewLines();
        reconcileNow();
      });
    });

    observer.observe(drawer, { childList: true, subtree: true });
  }

  /* ── 3b. Orphaned Double Paint ────────────────────────────
     A Double Paint line is an add-on to a kit in the same colour,
     never a product in its own right. Adding a kit, taking its
     bottle and then deleting the kit leaves a bottle of custom
     mixed paint for a vehicle with no kit: most of what a kit is
     worth, and all of the work, for the price of an add-on.

     Shopify already recalculates the Buy X Get Y correctly, so
     nothing is given away free. This is about the $29 bottle that
     should never have survived its kit.

     Liquid decides what counts as an orphan and says so with a
     hidden marker, in snippets/cart-drawer.liquid and
     sections/main-cart-footer.liquid. Reading a marker rather than
     fetching the cart on every drawer mutation keeps the common
     case, where nothing is wrong, free.

     Removal here is the enforcement. The gate in those two files
     disables our own checkout button and withholds the wallet buttons,
     and that is all a theme can do: /checkout is Shopify's, no theme
     code runs there, so a bottle that is still in the cart when the
     customer reaches it ships. Do not write "anything this misses
     still cannot check out" here again; it was written once and read
     as a guarantee.

     Order #1412 (2026-09-09) shipped a Touareg kit beside a Golf
     bottle. The removal paths themselves work: drawer and /cart page
     each took the bottle with the kit inside a second when tested on
     live. The hole was the promo auto-add below, which pressed a
     button from a render made before the customer deleted that kit,
     leaving the drawer one render behind the cart with its Checkout
     button enabled. Full account in
     docs/incidents/2026-09-09-orphan-double-paint-order-1412.md.

     Three layers now, because none of them can see the whole path:
     - reconcileNow() on every cart mutation and every render this
       script performs, asking the cart rather than trusting a marker;
     - reconcileIfNeeded() at page load, marker gated so an ordinary
       view costs nothing;
     - the checkout interceptor in layout/theme.liquid calls
       CFCart.reconcile() on every checkout click, so a button that is
       somehow live above an orphaned cart still cannot leave with it.
     What none of them reach: a cart edited with no page interaction,
     and anything after /checkout. A Shopify Flow rule on order
     creation is the backstop for that. */

  function colourKeyOf(props) {
    if (!props) return '';

    var raw = props['Paint Code'] || props['_Hex'] || props['Color Name'] || '';
    return String(raw).trim().toLowerCase();
  }

  var reconciling = false;

  function hasOrphanMarker() {
    return !!document.querySelector('[data-cf-orphan-paint]');
  }

  function reconcileOrphanPaint() {
    if (reconciling) return Promise.resolve(false);
    reconciling = true;

    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    return fetch(base + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var items = cart.items || [];
        var kitColours = {};
        var i;

        for (i = 0; i < items.length; i++) {
          if (items[i].product_type !== 'Kit') continue;
          var kitKey = colourKeyOf(items[i].properties);
          if (kitKey) kitColours[kitKey] = true;
        }

        var orphans = [];

        for (i = 0; i < items.length; i++) {
          if (items[i].handle !== 'double-paint') continue;

          var key = colourKeyOf(items[i].properties);

          /* No colour on the line at all. Nothing to judge it by, and
             deleting something a customer chose on a guess is worse
             than leaving it. Liquid lets these pass too. */
          if (!key) continue;

          if (!kitColours[key]) orphans.push(items[i].key);
        }

        if (!orphans.length) return false;

        return orphans
          .reduce(function (chain, lineKey) {
            return chain.then(function () {
              return fetch(base + 'cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: lineKey, quantity: 0 }),
              });
            });
          }, Promise.resolve())
          .then(function () {
            return true;
          });
      })
      .catch(function (err) {
        console.error('[CFCart] could not reconcile paint lines:', err);
        return false;
      })
      .then(function (removed) {
        reconciling = false;
        return removed;
      });
  }

  function afterReconcile(removed) {
    if (!removed) return false;

    /* The cart page renders its own sections and knows nothing about
       our fetch, so it has to be told. The drawer re-reads the cart
       every time it opens, so a closed one needs nothing. */
    if (/^\/cart(\/|$)/.test(window.location.pathname)) {
      window.location.reload();
      return true;
    }

    var drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.classList.contains('active')) return fetchAndOpen().then(function () { return true; });

    return true;
  }

  /* Ask the CART, not the page. Use this on every cart mutation.

     The marker is rendered in time on the paths a customer drives by
     hand (Dawn re-renders from the post-change cart), but a bottle add
     can land after the markup in hand was fetched. Asking the cart
     removes the dependency on render order. One cart.js request per
     cart change is the cost; cart changes are user paced and already
     trigger a section fetch, so this is not a hot path. `reconciling`
     keeps overlapping calls out, and a pass that removes something
     re-renders and runs again, finds nothing, and stops. */
  function reconcileNow() {
    return reconcileOrphanPaint().then(afterReconcile);
  }

  /* Marker gated, for page load only. Here the marker is doing useful
     work: it keeps an ordinary page view, where nothing is wrong and
     nothing has changed, from costing a cart fetch. */
  function reconcileIfNeeded() {
    if (!hasOrphanMarker()) return Promise.resolve(false);

    return reconcileNow();
  }

  /* ── 4. Add-on buttons ───────────────────────────────────
     One delegated handler for every [data-cf-add] in the drawer:
     the two rows inside "Add more items" and the per-line
     "Double the paint".

     Properties arrive as separate escaped data attributes rather
     than a JSON blob, so nothing has to be parsed back out of an
     attribute. Blank ones are dropped, which is what keeps a
     normal kit from gaining an empty Entry Type and lets a manual
     entry carry its verify-before-mixing flag through.

     The button is disabled while the request is in flight so a
     double tap cannot add twice, and CFCart.open() re-renders the
     drawer afterwards, which is what removes the row that was
     just used and reveals the new line. */

  var PROP_ATTRS = {
    'data-cf-year': 'Vehicle Year',
    'data-cf-make': 'Vehicle Make',
    'data-cf-model': 'Vehicle Model',
    'data-cf-cname': 'Color Name',
    'data-cf-code': 'Paint Code',
    'data-cf-hex': '_Hex',
    'data-cf-manual': 'Entry Type',
    'data-cf-delivery': 'Delivery',
  };

  function propertiesFor(btn) {
    var props = {};
    var found = false;

    for (var attr in PROP_ATTRS) {
      if (!Object.prototype.hasOwnProperty.call(PROP_ATTRS, attr)) continue;
      var val = btn.getAttribute(attr);
      if (val === null || val === '') continue;
      props[PROP_ATTRS[attr]] = val;
      found = true;
    }

    return found ? props : null;
  }

  function addFromButton(btn) {
    var id = parseInt(btn.getAttribute('data-cf-add'), 10);
    if (!id) return;

    var body = { id: id, quantity: 1 };
    var props = propertiesFor(btn);
    if (props) body.properties = props;

    btn.disabled = true;

    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    return fetch(base + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Add failed: ' + res.status);
        return res.json();
      })
      .then(function () {
        return fetchAndOpen();
      })
      .catch(function (err) {
        console.error('[CFCart] add-on add failed:', err);
        btn.disabled = false;
        window.location.href = CART_URL;
      });
  }

  function bindAddButtons() {
    document.addEventListener('click', function (evt) {
      var btn = evt.target.closest ? evt.target.closest('[data-cf-add]') : null;
      if (!btn) return;
      if (!btn.closest('cart-drawer')) return;
      evt.preventDefault();
      addFromButton(btn);
    });
  }

  /* ── 4b. Re-engagement promo: the free bottle ─────────────
     The campaign email promises a free Double Paint. The customer
     was told it is theirs, so making them find and press a button
     to claim it is a way to lose it: the Aug 5 note on this offer
     is explicit that a card still reading "+$29" gets backed out
     of even when it is pre-offered.

     So it is added for them, exactly once, by pressing the very
     button they would have pressed. Reusing the real button rather
     than assembling a second add path is deliberate: the colour
     properties (Vehicle Year/Make/Model, Color Name, Paint Code,
     _Hex, Entry Type) are the only thing telling the shop floor
     what to mix, and they are already correct on that button,
     inherited from the kit line directly above it.

     Every kit line is owed one, so the guard is a list of claimed
     line keys on a cart attribute rather than a JS flag or a single
     cart wide boolean: the drawer re-renders and the page can
     reload, and a second colour added ten minutes later is owed its
     bottle just as much as the first. A customer who removes one has
     decided something, and its key stays on the list so it does not
     come back.

     Failure here is silent on purpose. If it does not fire, the
     button is still sitting there reading FREE and the customer
     can press it. Nothing about the order is blocked. */

  var promoClaims = null;

  /* The claim list, read once per page load off the marker and then kept
     here. Re-reading it from the DOM after each add would race the
     re-render and could drop a key, which would auto-add a bottle the
     customer had deleted. */
  function promoClaimsList() {
    if (promoClaims) return promoClaims;

    var marker = document.querySelector('[data-cf-promo="doublepaint"]');
    var raw = (marker && marker.getAttribute('data-cf-promo-claims')) || '';
    promoClaims = raw ? raw.split('|') : [];

    return promoClaims;
  }

  function recordClaim(key) {
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var claims = promoClaimsList();

    claims.push(key);

    return fetch(base + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { cf_promo_dp_claims: claims.join('|') } }),
    });
  }

  var promoRunning = false;

  function kitLineStillInCart(key) {
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    return fetch(base + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        return (cart.items || []).some(function (item) {
          return item.key === key && item.product_type === 'Kit';
        });
      })
      .catch(function () {
        /* Unreadable cart: do not add. A missing free bottle is a button
           the customer can still press; a bottle with no kit is a refund. */
        return false;
      });
  }

  function applyPromo(root) {
    if (promoRunning) return;

    var scope = root || document;
    if (!scope.querySelector('[data-cf-promo="doublepaint"]')) return;

    /* Liquid has already withheld data-cf-promo-auto from any kit line whose
       key is on the list, so anything still carrying it is genuinely owed a
       bottle. The list is re-checked here too because the DOM in hand can be
       one render behind a claim this page made. */
    var claims = promoClaimsList();
    var btns = scope.querySelectorAll('.cf-dbl[data-cf-promo-auto]');
    var btn = null;
    var key = null;

    for (var i = 0; i < btns.length; i++) {
      var candidate = btns[i].getAttribute('data-cf-promo-auto');
      if (candidate && claims.indexOf(candidate) === -1) {
        btn = btns[i];
        key = candidate;
        break;
      }
    }

    if (!btn) return;

    promoRunning = true;

    /* Record the claim first. If the add then fails, the customer loses a
       free item they can still take by pressing the button, which now reads
       FREE. The other order can add a bottle somebody had deliberately
       deleted, and only one of those is recoverable by the customer.

       Then prove the kit is still there before pressing the button. `btn`
       came from a render, and by the time the claim has round-tripped the
       customer may have deleted the kit line it sits on. The button still
       carries that kit's colour, so pressing it would put a bottle for a
       vehicle with no kit into the cart, and nothing would re-render the
       drawer to say so until this chain's own fetchAndOpen, which a
       checkout click can beat. That is the shape of order #1412 on
       2026-09-09: two kit lines claimed, the removed kit's bottle shipped,
       the surviving kit's bottle never added. The key IS the kit line's
       key, so the check is exact. Skipping leaves the claim recorded,
       which is right: the customer removed that kit on purpose. */
    recordClaim(key)
      .then(function () {
        return kitLineStillInCart(key);
      })
      .then(function (present) {
        if (!present) {
          console.warn('[CFPromo] kit line gone before its free bottle was added; skipping', key);
          return null;
        }
        return addFromButton(btn);
      })
      .catch(function (err) {
        console.error('[CFPromo] could not claim the free Double Paint:', err);
      })
      .then(function () {
        promoRunning = false;

        /* The re-render fires its own applyPromo, but on a timer that can
           land while this one still holds the lock. Running again here is
           what carries a second or third kit through. It terminates: every
           pass either claims a key, which removes that button from the next
           render, or finds nothing and returns. */
        applyPromo(document);
      });
  }

  /* ── 5. Public API ────────────────────────────────────────
     window.CFCart.open() pulls fresh drawer markup through the
     Section Rendering API, hands it to Dawn's own renderContents
     (which repaints and opens), and falls back to a /cart page load
     if anything is missing or the request fails. The fallback is the
     point: no path may end with the customer stuck. */

  function fetchAndOpen() {
    var drawer = document.querySelector('cart-drawer');

    if (!drawer || typeof drawer.renderContents !== 'function') {
      window.location.href = CART_URL;
      return Promise.resolve();
    }

    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    return fetch(base + '?sections=cart-drawer,cart-icon-bubble', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
        return res.json();
      })
      .then(function (sections) {
        /* Dawn keys the whole empty layout off `is-empty` on <cart-drawer>,
           and renderContents only swaps .drawer__inner, so the flag has to
           be set here from the markup that just arrived. This used to
           always REMOVE it, on the reasoning that an add leaves the cart
           non-empty; but reconcile also lands here after removing the
           last bottle from a cart whose kit just went, and the cart is
           then empty. Removing the flag then hid both the empty state and
           the items block: a blank drawer with nothing but the title. */
        var markup = (sections && sections['cart-drawer']) || '';
        var empty = /<cart-drawer[^>]*\bis-empty\b/.test(markup);
        drawer.classList.toggle('is-empty', empty);

        drawer.renderContents({ sections: sections });

        /* renderContents opens on a setTimeout; repaint just after it.
           reconcileNow, not the marker: the markup in hand was fetched a
           moment ago and a bottle add can land after it. */
        setTimeout(function () {
          paintChips(drawer);
          markNewLines();
          reconcileNow();
          applyPromo(drawer);
        }, 0);
      })
      .catch(function (err) {
        console.error('[CFCart] falling back to the cart page:', err);
        window.location.href = CART_URL;
      });
  }

  window.CFCart = {
    open: fetchAndOpen,
    /* Opens without refetching. Use when the cart has not changed. */
    show: function () {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      } else {
        window.location.href = CART_URL;
      }
    },
    repaint: paintChips,
    /* Removes any Double Paint whose colour has no kit in the cart and
       resolves true if it removed something. The checkout interceptor
       in layout/theme.liquid calls this on every checkout click. */
    reconcile: reconcileNow,
  };

  /* The cart page swaps its own sections on every quantity change and
     removal, well outside the drawer the observer above is watching. */
  function watchCartPage() {
    var footer = document.getElementById('main-cart-footer');
    if (!footer) return;

    var queued = false;
    var observer = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        reconcileNow();
      });
    });

    observer.observe(footer, { childList: true, subtree: true });
  }

  function init() {
    watch();
    bindAddButtons();
    /* Covers the cart page, which has no drawer to observe, and any
       arrival on a cart that was left orphaned in an earlier session. */
    reconcileIfNeeded();
    watchCartPage();
    /* Covers the reload case: someone lands with the promo already on
       the cart and a kit already in it, without adding anything. */
    applyPromo(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
